import React, { useEffect, type ComponentType } from 'react';
import { useQueryLoader } from 'react-relay';
import KnowledgeBaseHubIconDI from '@atlaskit/icon/glyph/book';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import Spinner from '@atlaskit/spinner';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { NestingMenuItem as NestingMenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { matchPartialUrlPathname as matchPartialUrlPathnameDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import KnowledgeTypeQuery, {
	type mainSideNavKBTypeCheckerQuery,
} from '@atlassian/jira-relay/src/__generated__/mainSideNavKBTypeCheckerQuery.graphql.ts';
import { fg } from '@atlassian/jira-feature-gating';
import { useContainerAri } from '@atlassian/jira-servicedesk-knowledge-common/src/common/use-container/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { KNOWLEDGE_HUB_TYPE, KNOWLEDGE_HUB_NESTED_TYPE } from '../../constants.tsx';
import { KnowledgeHubMenu as KnowledgeHubMenuDI } from './hub-menu/main.tsx';
import { messages } from './messages.tsx';
import type { KnowledgeItemProps } from './types.tsx';
import type { KnowledgeHubMenuProps } from './hub-menu/types.tsx';

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4
 */

export const KnowledgeItem = ({
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	KnowledgeBaseHubIcon = KnowledgeBaseHubIconDI,
	KnowledgeHubMenu = KnowledgeHubMenuDI,
	MenuItem = MenuItemDI,
	NestingMenuItem = NestingMenuItemDI,
	matchPartialUrlPathname = matchPartialUrlPathnameDI,
	href,
	nestingId,
	overrides,
	stack,
	type,
	// eslint-disable-next-line react-hooks/react-compiler
	useIntl = useIntlDI,
}: KnowledgeItemProps) => {
	// eslint-disable-next-line react-hooks/react-compiler
	const { formatMessage } = useIntl();
	if (shouldRenderNesting(type === KNOWLEDGE_HUB_NESTED_TYPE, stack, nestingId)) {
		return (
			<NestingMenuItem
				analytics={{ itemId: 'knowledge-hub-nested' }}
				href={href}
				iconBefore={<KnowledgeBaseHubIcon label="" />}
				id={nestingId}
				overrides={overrides}
				selectedOn={matchPartialUrlPathname()}
				title={formatMessage(messages.itemKnowledgeBaseHub)}
			>
				<ErrorBoundary
					id="SideNavigationKnowledge"
					packageName="jira-navigation-apps-sidebar-servicedesk"
				>
					<KnowledgeItemWrapper KnowledgeHubMenu={KnowledgeHubMenu} href={href} />
				</ErrorBoundary>
			</NestingMenuItem>
		);
	}

	if (type === KNOWLEDGE_HUB_TYPE) {
		return (
			<MenuItem
				analytics={{ itemId: 'knowledge-hub' }}
				selectedOn={matchPartialUrlPathname()}
				iconBefore={<KnowledgeBaseHubIcon label="" />}
				href={href}
			>
				{formatMessage(messages.itemKnowledgeBase)}
			</MenuItem>
		);
	}

	return (
		<MenuItem
			analytics={{ itemId: 'knowledge-base' }}
			selectedOn={matchPartialUrlPathname()}
			iconBefore={<KnowledgeBaseHubIcon label="" />}
			href={href}
		>
			{formatMessage(messages.itemKnowledgeBase)}
		</MenuItem>
	);
};

export const KnowledgeItemWrapper = ({
	href,
	KnowledgeHubMenu,
}: {
	href: string;
	KnowledgeHubMenu: ComponentType<KnowledgeHubMenuProps>;
}) => {
	const [queryReference, loadQuery] =
		useQueryLoader<mainSideNavKBTypeCheckerQuery>(KnowledgeTypeQuery);

	const cloudId = useCloudId();
	const container = useContainerAri();

	useEffect(() => {
		loadQuery({
			cloudId,
			container,
			isEnabled: fg('kb-cross-site-enabled') || fg('3p-integration-enabled_t5ysh'),
		});
	}, [loadQuery, cloudId, container]);

	return (
		<Placeholder name="SideNavigationKnowledgeItem" fallback={<Spinner />}>
			{queryReference ? <KnowledgeHubMenu basePath={href} queryReference={queryReference} /> : null}
		</Placeholder>
	);
};
